import React, { useState } from 'react';
import StatItem from '../StatItem';
import { useCalculatorContext } from '@/components/DesignSystem/Subsections/Calculator/contexts';
import tagularEvent from '@/utils/tagular/tagularEvent';
import { ElementClicked } from '@/types/Tagular';
import uuid from '@/utils/uuid';
import { Chevron } from '@/components/DesignSystem/Icons';

interface DegreeLengthCardProps {
  title?: string;
  studyTime?: string;
  classesPerSemester?: number;
  semestersPerYear?: number;
  className?: string;
  isRecommended?: boolean;
}

const DegreeLengthCard: React.FC<DegreeLengthCardProps> = ({
  title = 'Full-Time Traditional',
  studyTime = '36 hrs/week',
  classesPerSemester = 4,
  semestersPerYear = 2,
  className = '',
  isRecommended = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(isRecommended);
  const { getCalculatorValue } = useCalculatorContext();

  const borderClass = isRecommended
    ? 'border-2 border-gray-800'
    : 'border border-gray-300';

  const toggleExpanded = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);

    // Track the expand/collapse action
    const clickPayload: ElementClicked = {
      correlationId: uuid(),
      actionOutcome: newExpandedState ? 'expand' : 'collapse',
      outboundUrl: '',
      webElement: {
        elementType: 'button',
        htmlId: 'degree-length-card',
        location: 'calculator',
        name: 'degree-length-card-toggle',
        position: '',
        text: title,
      },
    };

    tagularEvent('ElementClicked', clickPayload);
  };

  const subtitle = (
    <span>
      {classesPerSemester} classes per semester • {semestersPerYear} semesters
      per year
    </span>
  );
  const creditsPerClass = 3;
  const hoursPerClassWeek = 8;

  const creditsCompleted = getCalculatorValue('creditsCompleted');
  const totalCredits = getCalculatorValue('totalCreditsRequired');
  const degreeType = getCalculatorValue('degree-type');
  const creditsRemaining = totalCredits - creditsCompleted;

  // Calculations
  const creditsPerSemester = classesPerSemester * creditsPerClass;
  const semestersNeeded = Math.ceil(creditsRemaining / creditsPerSemester);
  const yearsToComplete = Math.ceil(semestersNeeded / semestersPerYear); // Round to int
  const studyTimePerWeek = classesPerSemester * hoursPerClassWeek;
  const totalDuration = creditsRemaining / creditsPerSemester;
  const degreeTypeString =
    degreeType.charAt(0).toUpperCase() +
    degreeType.slice(1) +
    (degreeType !== 'doctorate' ? "'s" : '');

  return (
    <div className={`p-4 rounded-lg ${borderClass} ${className}`}>
      <div className='cursor-pointer' onClick={toggleExpanded}>
        <div className='flex flex-row justify-between items-center'>
          <div className='font-body-18 font-bold'>{title}</div>
          <div className='flex items-center gap-2'>
            {isRecommended && (
              <div className='bg-tertiary-700 text-white px-3 py-1 rounded-full text-sm font-bold'>
                Recommended
              </div>
            )}
            <div className='text-gray-500 transition-transform duration-200 ease-in-out'>
              <Chevron
                direction={isExpanded ? 'up' : 'down'}
                width={12}
                height={12}
              />
            </div>
          </div>
        </div>
        <div>{subtitle}</div>
      </div>

      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mt-4'>
          <StatItem
            label='Completion Time'
            value={`${yearsToComplete} years to complete your ${degreeTypeString} degree`}
          />
          <StatItem label='Study Time' value={`${studyTimePerWeek} hrs/week`} />
          <StatItem
            label='Per Semester'
            value={`${classesPerSemester} classes`}
          />
          <StatItem
            label='Total Duration'
            value={`${semestersNeeded} semesters`}
          />
        </div>
      </div>
    </div>
  );
};

export default DegreeLengthCard;
